/*
* Service for sending analytics and tracking events.
*
* Responsibilities:
* - Send view, playback, click, and interaction events
* - Send Web Vitals metrics
*/
import type { ApiClient } from "@/common/api/http/types";
import { EventSource, RevenueLinkType } from "@/common/buses";

export default class MetricsService {
  public constructor(private readonly client: ApiClient) {}

  async sendGifView(params: {
    gif: string;
    source: EventSource;
    source_id?: string | null;
    boosted: boolean;
    promoted: boolean;
    username?: string | null;
    position: number;
    fullscreen: boolean;
    pathname: string;
    context?: string | null;
    reason?: string | null;
  }): Promise<void> {
    return await this.sendBeacon("gifs", { event: "view", ...params });
  }

  async sendGifPlayback(params: {
    gif: string;
    source: EventSource;
    boosted: boolean;
    play_count: number;
    start_position: number;
    username?: string | null;
    position?: number | null;
    duration?: number;
    pathname: string;
  }): Promise<void> {
    return await this.sendBeacon("gif-playback", params);
  }

  async sendGifVideoLoad(params: {
    duration: number;
    source: EventSource;
  }): Promise<void> {
    return await this.sendBeacon("load-gif-video", params);
  }

  async sendGifShared(params: {
    gif: string;
    source: EventSource;
    boosted: boolean;
    position?: number;
    play_count?: number;
    username?: string | null;
    pathname: string;
  }): Promise<void> {
    return await this.sendBeacon("share-gif", params);
  }

  async sendGifAddedToCollection(params: {
    gif: string;
    source: EventSource;
    boosted: boolean;
    position?: number;
    play_count?: number;
    username?: string | null;
    pathname: string;
  }): Promise<void> {
    return await this.sendBeacon("add-to-collection", params);
  }

  async sendGifLiked(params: {
    gif: string;
    source: EventSource;
    boosted: boolean;
    position?: number;
    play_count?: number;
    username?: string | null;
    pathname: string;
  }): Promise<void> {
    return await this.sendBeacon("like-gif", params);
  }

  async sendProfileClicked(params: {
    gif: string;
    creator: string;
    source: EventSource;
    source_id: string | null;
    position: number;
    play_count?: number;
    username?: string | null;
    pathname: string;
    promoted: boolean;
  }): Promise<void> {
    return await this.sendBeacon("click-profile", params);
  }

  async sendUserSubscription(params: {
    gif: string;
    source: EventSource;
    position?: number;
    play_count?: number;
    username?: string | null;
    pathname: string;
  }): Promise<void> {
    return await this.sendBeacon("follow-user", params);
  }

  async sendVisit({ username, path }: { username?: string; path: string }): Promise<void> {
    return await this.sendBeacon("visit", {
      username,
      referrer: document.referrer || null,
      path,
    });
  }

  async sendWebVitalsMetrics(params: {
    fcp?: number;
    lcp?: number;
    cls?: number;
    inp?: number;
    ttfb?: number;
    page_group: string;
    device_type: string;
    experiments: string[];
  }): Promise<void> {
    return await this.sendBeacon("web-vitals", params);
  }

  async sendLiveCircleClicked(params: {
    device: string;
  }): Promise<void> {
    return await this.sendBeacon("click-live-circle", params);
  }

  async sendNicheSearched(params: {
    niche: string;
  }): Promise<void> {
    return await this.sendBeacon("search-niche", params);
  }

  async sendLiveCamClicked(params: {
    creator: string;
    video: string;
    position: number;
  }): Promise<void> {
    return await this.sendBeacon("click-live-cam", params);
  }

  async sendLiveCamView(params: {
    creator: string;
    video: string;
    position: number;
  }): Promise<void> {
    return await this.sendBeacon("view-live-cam", params);
  }

  async sendPaidLinksClicked(params: {
    username: string;
    source: EventSource;
    source_id?: string | null;
  }): Promise<void> {
    return await this.sendBeacon("click-paid-link", params);
  }

  async sendPaidLinksViewed(params: {
    username: string;
    source: EventSource;
    source_id?: string | null;
  }): Promise<void> {
    return await this.sendBeacon("view-paid-link", params);
  }

  async sendRevenueLinkClicked(params: {
    username: string;
    platform: string | null;
    gif: string;
    link_type: RevenueLinkType;
  }): Promise<void> {
    return await this.sendBeacon("click-revenue-link", params);
  }

  async sendFeedSwitched(params: {
    src: string;
    dest: string;
  }): Promise<void> {
    return await this.sendBeacon("switch-feed", params);
  }

  protected async sendBeacon<Response, Params>(action: string, data: Params): Promise<Response | undefined> {
    const session = await this.client.getSessionId();

    return await this.client.beacon<Response, Params>(`v2/metrics/${action}`, {
      session,
      ...data,
    });
  }
}
